import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useLang } from '../LangContext';
// SocialIcons removed — using old global Footer
import AdBanner from '../components/AdBanner';
import API from '../api';
import { categoryImage } from '../constants';

const Reveal = ({ children, width = "100%", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  return (
    <div ref={ref} style={{ position: "relative", width, overflow: "hidden" }}>
      <motion.div
        variants={{ hidden: { opacity: 0, y: 75 }, visible: { opacity: 1, y: 0 } }}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay }}
      >
        {children}
      </motion.div>
    </div>
  );
};

const MagneticBtn = ({ children, to, className, style }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const handle = (e) => {
    const b = ref.current.getBoundingClientRect();
    setPos({ x: (e.clientX - (b.left + b.width / 2)) * 0.3, y: (e.clientY - (b.top + b.height / 2)) * 0.3 });
  };
  const reset = () => setPos({ x: 0, y: 0 });
  return (
    <motion.div ref={ref} onMouseMove={handle} onMouseLeave={reset} animate={{ x: pos.x, y: pos.y }} transition={{ type: "spring", stiffness: 150, damping: 15, mass: 0.1 }}>
      <Link to={to} className={className} style={style}>{children}</Link>
    </motion.div>
  );
};

function StatCard({ value, label, delay }) {
  const [count, setCount] = useState(0);
  const numeric = parseInt(value, 10);
  const suffix = value.replace(String(numeric), '');
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (inView && numeric > 0) {
      const step = Math.max(Math.floor(2000 / numeric), 20);
      const t = setInterval(() => { setCount(c => { const n = c + 1; return n >= numeric ? (clearInterval(t), numeric) : n; }); }, step);
      return () => clearInterval(t);
    }
  }, [inView, numeric]);
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay }} style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 28, fontWeight: 900, color: 'var(--primary)', marginBottom: 4 }}>{count}{suffix}</div>
      <div style={{ color: 'var(--text-muted)', fontSize: 13, fontWeight: 600 }}>{label}</div>
    </motion.div>
  );
}

// ─── Icons ───
const Icon = ({ children, w = 22 }) => <svg width={w} height={w} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>;
const Hi = () => <Icon><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></Icon>;
const Bi = () => <Icon><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></Icon>;
const Si = () => <Icon><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></Icon>;
const Ai = () => <Icon><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></Icon>;
const Ci = () => <Icon><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></Icon>;
const Ni = () => <Icon><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/><line x1="8" y1="7" x2="16" y2="7"/><line x1="8" y1="11" x2="14" y2="11"/></Icon>;
const Ti = () => <Icon><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></Icon>;
const SearchIcon = () => <Icon w={20}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></Icon>;
const BellIcon = () => <Icon><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></Icon>;
const ChevronDown = () => <Icon><polyline points="6 9 12 15 18 9"/></Icon>;
const Star = ({ f }) => <svg width="14" height="14" viewBox="0 0 24 24" fill={f ? '#FF7A00' : '#E5E7EB'} stroke={f ? '#FF7A00' : '#E5E7EB'} strokeWidth="1"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
const QuoteIcon = () => <svg width="36" height="36" viewBox="0 0 24 24" fill="var(--primary)" opacity="0.15"><path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10H14.017zM0 21v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151C7.563 6.068 6 8.789 6 11h4v10H0z"/></svg>;

// ─── Styles ───
const s = {
  section: { padding: '40px 0' },
  container: { maxWidth: 1200, margin: '0 auto', padding: '0 32px' },
  tag: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,122,0,0.08)', color: 'var(--primary)', padding: '6px 14px', borderRadius: 100, fontWeight: 800, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 },
  h2: { fontSize: 22, fontWeight: 700, color: 'var(--secondary)', marginBottom: 10 },
  h3: { fontSize: 18, fontWeight: 700, color: 'var(--secondary)', marginBottom: 8 },
};

const StarRow = ({ n = 5 }) => <div style={{ display: 'flex', gap: 2 }}>{[1,2,3,4,5].map(i => <Star key={i} f={i <= n} />)}</div>;

// ─── Category icon/color map ───
const CATEGORY_ICONS = {
  'electronics': { icon: '📱', color: '#EEF2FF' },
  'fashion': { icon: '👗', color: '#FEF2F2' },
  'home & living': { icon: '🏠', color: '#FEFCE8' },
  'food & beverage': { icon: '🍽️', color: '#FFF7ED' },
  'beauty & health': { icon: '💄', color: '#FDF2F8' },
  'sports & outdoors': { icon: '⚽', color: '#F0FDF4' },
  'books & media': { icon: '📚', color: '#F5F3FF' },
  'services': { icon: '🔧', color: '#ECFEFF' },
  'clothing': { icon: '👗', color: '#FEF2F2' },
  'food': { icon: '🍽️', color: '#FFF7ED' },
  'health': { icon: '💄', color: '#FDF2F8' },
  'technology': { icon: '💻', color: '#EEF2FF' },
  'automotive': { icon: '🚗', color: '#F0F9FF' },
  'agriculture': { icon: '🌾', color: '#F0FDF4' },
  'construction': { icon: '🏗️', color: '#FFF7ED' },
};
const DEFAULT_CATEGORY_ICON = { icon: '📦', color: '#F3F4F6' };

// ─── Testimonials Data ───
const testimonials = [
  { name: 'Abebe Kebede', business: 'Kebede Electronics', text: 'All Things transformed our online presence. Our sales have doubled since joining the Bazaar marketplace.' },
  { name: 'Selam Tesfaye', business: 'Selam Fashion', text: 'The platform made it so easy to reach customers across Ethiopia. Highly recommended for any MSME!' },
  { name: 'Dawit Haile', business: 'Haile Foods', text: 'Professional support team and great tools for managing our online store. A game-changer for our business.' },
  { name: 'Meron Alemu', business: 'Meron Beauty', text: 'From registration to selling, the process was smooth. Our customer base has expanded significantly.' },
];

// ─── Partners Data ───
const partners = [
  { name: 'Commercial Bank of Ethiopia', logo: '/logos/abay.png', initials: 'CBE', color: '#0066B3', bg: '#E6F0FA' },
  { name: 'Dashen Bank', logo: '/logos/dashn.jfif', initials: 'DB', color: '#00529B', bg: '#E6F0F8' },
  { name: 'Awash Bank', logo: '/logos/awash.png', initials: 'AB', color: '#00875A', bg: '#E6F5EE' },
  { name: 'Bank of Abyssinia', logo: '/logos/abisnya.jfif', initials: 'BOA', color: '#C41E3A', bg: '#FDE8EC' },
  { name: 'Hibret Bank', logo: '/logos/hbret.png', initials: 'HB', color: '#FF6600', bg: '#FFF0E6' },
  { name: 'Zemen Bank', logo: '/logos/zemen.jfif', initials: 'ZB', color: '#7B2D8E', bg: '#F3E8F7' },
];

// ─── Section Components ───

const TopHeader = () => {
  const { t } = useLang();
  const isSeller = !!localStorage.getItem('userToken');
  const isBuyer = !!localStorage.getItem('buyerToken');
  const isLogged = isSeller || isBuyer;

  let initial = '?';
  if (isLogged) {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      const buyer = JSON.parse(localStorage.getItem('buyer') || 'null');
      const profile = user || buyer;
      const fullName = profile?.full_name || profile?.company_name || '';
      initial = fullName.trim().charAt(0).toUpperCase() || (isSeller ? 'S' : 'B');
    } catch { initial = isSeller ? 'S' : 'B'; }
  }

  return (
    <header style={{
      position: 'sticky', top: 0, left: 0, right: 0, zIndex: 999,
      background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(16px)',
      borderBottom: '1px solid rgba(0,0,0,0.05)',
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '12px 32px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <a href={isLogged ? '/dashboard' : '/register'} style={{ whiteSpace: 'nowrap', padding: '8px 20px', background: 'var(--primary)', color: '#fff', borderRadius: 100, fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
          {isLogged ? t.mpTopDashboard : t.mpTopRegister}
        </a>

        <button style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
          <BellIcon />
          <span style={{ position: 'absolute', top: -2, right: -2, width: 8, height: 8, background: '#EF4444', borderRadius: '50%' }} />
        </button>

        <Link to={isSeller ? '/dashboard' : isBuyer ? '/buyer-dashboard' : '/login'} style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
          {initial}
        </Link>
      </div>
      </header>
  );
};

const HeroSection = ({ productCount = 0, businessCount = 0, cityCount = 0 }) => {
  const { t } = useLang();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  return (
    <section ref={ref} style={{ position: 'relative', overflow: 'hidden', background: 'linear-gradient(135deg, #FFF8F0 0%, #FFFFFF 50%, #F0F7FF 100%)' }}>
      <div style={s.container}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 40, minHeight: 'calc(72vh - 60px)', padding: '32px 0' }}>
          <div style={{ flex: '1 1 55%' }}>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.15, duration: 0.8 }} style={{ fontSize: 32, fontWeight: 900, lineHeight: 1.15, color: 'var(--secondary)', marginBottom: 16 }}>
              {t.mpHeroTitle} <span style={{ color: 'var(--primary)' }}>{t.mpHeroTitleHighlight}</span>
            </motion.h1>

            <motion.p initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.3, duration: 0.8 }} style={{ fontSize: 16, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 32, maxWidth: 540 }}>
              {t.mpHeroDesc}
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.45, duration: 0.8 }} style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}>
              <MagneticBtn to="/bazaar" className="btn-primary" style={{ fontSize: 15 }}>
                {t.mpStartShopping}
              </MagneticBtn>
              <MagneticBtn to="/register" className="btn-outline" style={{ fontSize: 15 }}>
                {t.mpRegisterBusiness}
              </MagneticBtn>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 0.6, duration: 0.8 }} style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
              <StatCard value={String(businessCount || 5)} label={t.mpStatBusinesses} delay={0.7} />
              <StatCard value={String(productCount || 50)} label={t.mpStatProducts} delay={0.8} />
              <StatCard value={String(cityCount || 5)} label={t.mpStatCities} delay={0.9} />
              <StatCard value="98%" label={t.mpStatSatisfaction} delay={1.0} />
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={inView ? { opacity: 1, scale: 1 } : {}} transition={{ delay: 0.3, duration: 1 }} style={{ flex: '1 1 45%', position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ width: '100%', maxWidth: 520, aspectRatio: '4/3', borderRadius: 40, overflow: 'hidden', position: 'relative', boxShadow: '0 40px 80px rgba(255,122,0,0.1)' }}>
              <img src="/images/home-background.png" alt="Marketplace" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(255,122,0,0.1) 0%, transparent 50%)' }} />
            </div>
            <div style={{ position: 'absolute', top: 20, right: -10, background: '#fff', borderRadius: 16, padding: '14px 20px', boxShadow: '0 8px 30px rgba(0,0,0,0.08)', textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--primary)' }}>{productCount || '50'}+</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.mpStatProducts}</div>
            </div>
            <div style={{ position: 'absolute', bottom: 30, left: -10, background: '#fff', borderRadius: 16, padding: '14px 20px', boxShadow: '0 8px 30px rgba(0,0,0,0.08)', textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--primary)' }}>{businessCount || '5'}+</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.mpStatBusinesses}</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const SearchBox = ({ categories = [], locations = [] }) => {
  const { t } = useLang();
  return (
    <section style={{ marginTop: -30, position: 'relative', zIndex: 5 }}>
      <div style={s.container}>
        <div style={{ background: '#fff', borderRadius: 20, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6, display: 'block' }}>{t.mpSearchCategory}</label>
              <select style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.08)', background: 'var(--bg-subtle)', fontSize: 13, color: 'var(--text-muted)' }}>
                <option>{t.mpSearchAllCategories}</option>
                {categories.map(c => <option key={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6, display: 'block' }}>{t.mpSearchLocation}</label>
              <select style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.08)', background: 'var(--bg-subtle)', fontSize: 13, color: 'var(--text-muted)' }}>
                <option>{t.mpSearchAllCities}</option>
                {locations.map(loc => <option key={loc}>{loc}</option>)}
              </select>
            </div>
            <div style={{ flex: '2 1 300px' }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6, display: 'block' }}>{t.mpSearchBtn}</label>
              <div style={{ position: 'relative' }}>
                <input placeholder={t.mpSearchKeyword} style={{ width: '100%', padding: '12px 14px 12px 40px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.08)', background: 'var(--bg-subtle)', fontSize: 13, outline: 'none' }} />
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }}><SearchIcon /></span>
              </div>
            </div>
            <button style={{ padding: '12px 32px', background: 'var(--primary)', color: '#fff', borderRadius: 10, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {t.mpSearchBtn}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

const TrustBadges = () => {
  const { t } = useLang();
  const badges = [
    { icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>, title: t.mpTrustVerified, desc: t.mpTrustVerifiedDesc },
    { icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>, title: t.mpTrustSecure, desc: t.mpTrustSecureDesc },
    { icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>, title: t.mpTrustReturns, desc: t.mpTrustReturnsDesc },
    { icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>, title: t.mpTrust247, desc: t.mpTrust247Desc },
  ];
  return (
    <section style={{ padding: '32px 0' }}>
      <div style={s.container}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
          {badges.map((b, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 24, background: '#fff', borderRadius: 16, border: '2px solid #FFE4C7', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
                <div style={{ width: 52, height: 52, background: 'rgba(255,122,0,0.08)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{b.icon}</div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 13, color: 'var(--secondary)', marginBottom: 2 }}>{b.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-light)' }}>{b.desc}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const CategoriesSection = ({ categories = [] }) => {
  const { t } = useLang();
  const displayCategories = categories.length > 0 ? categories : [
    { name: 'Electronics', icon: '📱', color: '#EEF2FF', count: 0 },
    { name: 'Fashion', icon: '👗', color: '#FEF2F2', count: 0 },
    { name: 'Food & Beverage', icon: '🍽️', color: '#FFF7ED', count: 0 },
    { name: 'Beauty & Health', icon: '💄', color: '#FDF2F8', count: 0 },
    { name: 'Home & Living', icon: '🏠', color: '#FEFCE8', count: 0 },
    { name: 'Sports & Outdoors', icon: '⚽', color: '#F0FDF4', count: 0 },
    { name: 'Books & Media', icon: '📚', color: '#F5F3FF', count: 0 },
    { name: 'Services', icon: '🔧', color: '#ECFEFF', count: 0 },
  ];
  return (
    <section style={{ padding: '32px 0 40px' }}>
      <div style={s.container}>
        <Reveal>
          <div style={s.tag}>{t.mpCatTag || 'Categories'}</div>
          <h2 style={s.h2}>{t.mpCatTitle} <span style={{ color: 'var(--primary)' }}>{t.mpCatTitleHighlight}</span></h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>{t.mpCatDesc}</p>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          {displayCategories.map((c, i) => {
            const img = categoryImage(c.name);
            return (
              <Reveal key={i} delay={i * 0.05}>
                <Link to="/bazaar" style={{ display: 'block', padding: 24, background: c.color, borderRadius: 20, textDecoration: 'none', transition: 'all 0.3s', border: '1px solid transparent' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {img ? (
                    <div style={{ width: 64, height: 64, borderRadius: 16, overflow: 'hidden', marginBottom: 10 }}>
                      <img src={img} alt={c.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  ) : (
                    <div style={{ fontSize: 32, marginBottom: 8 }}>{c.icon}</div>
                  )}
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--secondary)', marginBottom: 4 }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', fontWeight: 600 }}>{c.count} {t.mpProducts}</div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ─── BankLogo ───
const BankLogo = ({ src, fallback, color }) => {
  const [error, setError] = useState(false);
  if (error || !src) {
    return <span style={{ fontWeight: 900, fontSize: 16, color, letterSpacing: '0.02em' }}>{fallback}</span>;
  }
  return <img src={src} alt="" style={{ width: '80%', height: '80%', objectFit: 'contain' }} onError={() => setError(true)} />;
};

const Partners = () => {
  const { t } = useLang();
  return (
    <section style={{ padding: '40px 0', background: 'linear-gradient(180deg, var(--bg-subtle) 0%, #fff 100%)' }}>
      <div style={s.container}>
        <Reveal>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ ...s.tag, margin: '0 auto 16px' }}>{t.mpPartnersTag}</div>
            <h2 style={s.h2}>{t.mpPartnersTitle} <span style={{ color: 'var(--primary)' }}>{t.mpPartnersTitleHighlight}</span></h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>{t.mpPartnersDesc}</p>
          </div>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 16, maxWidth: 900, margin: '0 auto' }}>
          {partners.map((p, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div style={{
                padding: '24px 16px', background: '#fff', borderRadius: 20,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                border: '1px solid rgba(0,0,0,0.04)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                transition: 'all 0.3s', cursor: 'default',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.04)'; }}
              >
                <div className="partner-logo-wrap" style={{
                  width: 80, height: 80, borderRadius: 16, background: p.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 10, border: `2px solid ${p.color}15`,
                  overflow: 'hidden',
                }}>
                  <BankLogo src={p.logo} fallback={p.initials} color={p.color} />
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--secondary)', textAlign: 'center', lineHeight: 1.3 }}>{p.name}</div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <div style={s.tag}>{t.mpPaymentsTag}</div>
            <h2 style={s.h2}>{t.mpPaymentsTitle} <span style={{ color: 'var(--primary)' }}>{t.mpPaymentsTitleHighlight}</span></h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>{t.mpPaymentsDesc}</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
              {[
                { emoji: '💳', key: t.mpPaymentCreditCard },
                { emoji: '🏦', key: t.mpPaymentBankTransfer },
                { emoji: '📱', key: t.mpPaymentMobileMoney },
                { emoji: '💵', key: t.mpPaymentCashDelivery },
                { emoji: '✉️', key: t.mpPaymentTelebirr },
              ].map((m, i) => (
                <div key={i} style={{
                  padding: '12px 24px', background: '#fff', borderRadius: 100,
                  border: '1px solid rgba(0,0,0,0.06)', fontSize: 14, fontWeight: 600,
                  color: 'var(--secondary)', boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                  transition: 'all 0.3s', cursor: 'default',
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.04)'; }}
                >
                  {m.emoji} {m.key}
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const TestimonialsSection = () => {
  const { t } = useLang();
  return (
    <section style={{ padding: '40px 0' }}>
      <div style={s.container}>
        <Reveal>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ ...s.tag, margin: '0 auto 16px' }}>{t.mpTestimonialTag}</div>
            <h2 style={s.h2}>{t.mpTestimonialTitle} <span style={{ color: 'var(--primary)' }}>{t.mpTestimonialTitleHighlight}</span></h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>{t.mpTestimonialDesc}</p>
          </div>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24 }}>
          {testimonials.map((t, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div style={{ padding: 28, background: '#fff', borderRadius: 20, border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 4px 16px rgba(0,0,0,0.03)', position: 'relative' }}>
                <div style={{ position: 'absolute', top: 16, right: 20 }}><QuoteIcon /></div>
                <StarRow n={5} />
                <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6, margin: '12px 0 16px', fontStyle: 'italic' }}>"{t.text}"</p>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--secondary)' }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 600 }}>{t.business}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Newsletter = () => {
  const { t } = useLang();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true); setError('');
    try {
      await API.post('/newsletter/subscribe', { email: email.trim() });
      setSubscribed(true);
    } catch (err) {
      setSubscribed(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section style={{ padding: '40px 0', background: 'var(--secondary)' }}>
      <div style={s.container}>
        <Reveal>
          <div style={{ textAlign: 'center', color: '#fff', maxWidth: 600, margin: '0 auto' }}>
            <h2 style={{ color: '#fff', fontSize: 24, fontWeight: 700, marginBottom: 10 }}>{t.mpNewsletterTitle}</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>{t.mpNewsletterDesc}</p>
            {!subscribed ? (
              <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 12, maxWidth: 480, margin: '0 auto' }}>
                <input type="email" required placeholder={t.mpNewsletterPlaceholder} value={email} onChange={e => setEmail(e.target.value)} style={{ flex: 1, padding: '14px 20px', borderRadius: 12, border: 'none', fontSize: 14, outline: 'none' }} />
                <button type="submit" disabled={loading} style={{ padding: '14px 28px', background: 'var(--primary)', color: '#fff', borderRadius: 12, fontWeight: 700, fontSize: 14, border: 'none', cursor: loading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: loading ? 0.7 : 1 }}>
                  {loading ? '...' : t.mpNewsletterBtn}
                </button>
              </form>
            ) : (
              <div style={{ fontSize: 16, color: '#4ADE80', fontWeight: 600 }}>{t.mpNewsletterSuccess}</div>
            )}
            {error && <div style={{ fontSize: 13, color: '#FCA5A5', marginTop: 8 }}>{error}</div>}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ─── Main Export ───

export default function Home() {
  const [products, setProducts] = useState([]);
  const [managedCategories, setManagedCategories] = useState([]);

  useEffect(() => {
    API.get('/bazaar/products')
      .then(r => {
        const data = r.data;
        setProducts(Array.isArray(data) ? data : (Array.isArray(data?.products) ? data.products : []));
      })
      .catch(() => {});
    API.get('/bazaar/categories')
      .then(r => {
        if (Array.isArray(r.data)) setManagedCategories(r.data.map(c => c.name));
      })
      .catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const seen = new Map();
    products.forEach(p => {
      const cat = (p.category || '').trim();
      if (!cat) return;
      const key = cat.toLowerCase();
      if (!seen.has(key)) {
        const iconInfo = CATEGORY_ICONS[key] || DEFAULT_CATEGORY_ICON;
        seen.set(key, { name: cat, icon: iconInfo.icon, color: iconInfo.color, count: 0 });
      }
      seen.get(key).count++;
    });
    // Admin-managed categories appear on the site even before products exist for them.
    managedCategories.forEach(name => {
      const key = name.toLowerCase();
      if (!seen.has(key)) {
        const iconInfo = CATEGORY_ICONS[key] || DEFAULT_CATEGORY_ICON;
        seen.set(key, { name, icon: iconInfo.icon, color: iconInfo.color, count: 0 });
      }
    });
    return Array.from(seen.values()).sort((a, b) => b.count - a.count);
  }, [products, managedCategories]);

  const locations = useMemo(() => {
    const seen = new Set();
    products.forEach(p => {
      const loc = (p.seller_location || '').trim();
      if (loc) seen.add(loc);
    });
    return Array.from(seen).sort();
  }, [products]);

  const stats = useMemo(() => {
    const sellerIds = new Set();
    const citySet = new Set();
    products.forEach(p => {
      if (p.seller_id) sellerIds.add(p.seller_id);
      if (p.seller_location) citySet.add(p.seller_location);
    });
    return { businessCount: sellerIds.size || 5, productCount: products.length || 50, cityCount: citySet.size || 5 };
  }, [products]);

  return (
    <div style={{ position: 'relative', minHeight: '100vh', maxWidth: '100vw', overflowX: 'hidden' }} className="home-marketplace">
      <TopHeader />
      <section style={{ padding: '0', margin: 0 }}>
        <AdBanner />
      </section>
      <HeroSection productCount={stats.productCount} businessCount={stats.businessCount} cityCount={stats.cityCount} />
      <TrustBadges />
      <CategoriesSection categories={categories} />
      <Partners />
      <TestimonialsSection />

      <style>{`
        .home-marketplace { position: relative; min-height: 100vh; }

        @media (max-width: 1024px) {
          .home-marketplace [style*="flex: 1 1 55%"] { flex: 1 1 100% !important; }
          .home-marketplace [style*="flex: 1 1 45%"] { flex: 1 1 100% !important; }
          .home-marketplace [style*="min-height: calc(80vh"] { min-height: auto !important; }
          .home-marketplace [style*="gap: 60"] { gap: 32px !important; }
        }

        @media (max-width: 768px) {
          .home-marketplace [style*="grid-template-columns: repeat(6, 1fr)"] { grid-template-columns: repeat(3, 1fr) !important; }
          .home-marketplace [style*="grid-template-columns: repeat(4, 1fr)"] { grid-template-columns: repeat(2, 1fr) !important; }
          .home-marketplace [style*="grid-template-columns: repeat(3, 1fr)"] { grid-template-columns: repeat(2, 1fr) !important; }
          .home-marketplace [style*="grid-template-columns: repeat(2, 1fr)"] { grid-template-columns: 1fr !important; }
          .home-marketplace [style*="grid-template-columns: 2fr 1fr 1fr 1fr"] { grid-template-columns: 1fr 1fr !important; }
          .home-marketplace [style*="height: 600px"] { height: 300px !important; }
          .home-marketplace [style*="gap: 60"] { gap: 24px !important; }
          .home-marketplace [style*="gap: 48"] { gap: 24px !important; }
          .home-marketplace [style*="flex-direction: column"] { flex-direction: column !important; }
          .home-marketplace [style*="grid-template-columns: repeat(4"] { grid-template-columns: repeat(2, 1fr) !important; }
          .home-marketplace [style*="padding: '12px 32px'"] { padding: 10px 16px !important; }
          .home-marketplace [style*="padding: 12px 32px"] { padding: 10px 16px !important; }
          .home-marketplace [style*="maxWidth: 1200"] { padding: 0 16px !important; }
        }

        @media (max-width: 480px) {
          .home-marketplace h1 { font-size: 24px !important; line-height: 1.2 !important; }
          .home-marketplace h2 { font-size: 18px !important; }
          .home-marketplace section { padding: 16px 0 !important; }
          .home-marketplace [style*="grid-template-columns: repeat(4, 1fr)"] { grid-template-columns: repeat(2, 1fr) !important; }
          .home-marketplace [style*="grid-template-columns: repeat(3, 1fr)"] { grid-template-columns: 1fr !important; }
          .home-marketplace [style*="grid-template-columns: repeat(2, 1fr)"] { grid-template-columns: 1fr !important; }
          .home-marketplace [style*="grid-template-columns: 2fr 1fr 1fr 1fr"] { grid-template-columns: 1fr !important; }
          .home-marketplace [style*="padding: 28px"] { padding: 16px !important; }
          .home-marketplace [style*="padding: 24px"] { padding: 14px !important; }
          .home-marketplace [style*="padding: 12px 32px"] { padding: 10px 16px !important; }
          .home-marketplace [style*="margin: 0 auto"][style*="padding"] { padding: 0 16px !important; }
          .home-marketplace [style*="height: 600px"] { height: 220px !important; }
          .home-marketplace [style*="min-height: calc(80vh"] { min-height: auto !important; flex-direction: column !important; gap: 20px !important; padding: 16px 0 !important; }
          .home-marketplace .grid-2 { grid-template-columns: 1fr !important; }
          .home-marketplace .grid-3 { grid-template-columns: 1fr !important; }
          .home-marketplace [style*="display: flex"][style*="flexWrap: wrap"][style*="gap: 16"] { gap: 8px !important; }
          .home-marketplace [style*="display: flex"][style*="gap: 48"] { gap: 16px !important; }
          .home-marketplace [style*="borderRadius: 40"] { border-radius: 20px !important; }
          .home-marketplace [style*="borderRadius: 20"] { border-radius: 14px !important; }
          .home-marketplace [style*="fontSize: 42"] { font-size: 24px !important; }
          .home-marketplace [style*="maxWidth: 520"] { max-width: 100% !important; }
          .home-marketplace [style*="maxWidth: 600"] { max-width: 100% !important; }
          .home-marketplace [style*="maxWidth: 700"] { max-width: 100% !important; }
          .home-marketplace [style*="maxWidth: 480"] { max-width: 100% !important; }
          .home-marketplace [style*="minHeight: calc(80vh"] { min-height: auto !important; }
          /* TopHeader: stack items */
          .home-marketplace header > div { flex-wrap: wrap !important; justify-content: center !important; }
          /* Hero: stack left+right */
          .home-marketplace section:first-of-type + div [style*="flex: 1 1 55%"],
          .home-marketplace section:first-of-type + div [style*="flex: 1 1 45%"] { flex: 1 1 100% !important; text-align: center; }
          .home-marketplace [style*="display: flex"][style*="gap: 48"] { flex-wrap: wrap !important; justify-content: center !important; }
          /* Buttons full width */
          .home-marketplace .btn-primary,
          .home-marketplace .btn-outline { width: 100% !important; justify-content: center !important; padding: 14px 20px !important; }
          /* Stat cards */
          .home-marketplace [style*="display: flex"][style*="gap: 48"][style*="flexWrap"] { gap: 16px !important; }
          .home-marketplace [style*="display: flex"][style*="gap: 48"] > div { flex: 0 0 calc(50% - 8px) !important; }
          /* Floating stat cards */
          .home-marketplace [style*="position: absolute"][style*="top: 20"] { display: none !important; }
          .home-marketplace [style*="position: absolute"][style*="bottom: 30"] { display: none !important; }
          /* Partners 2-col on small */
          .home-marketplace [style*="repeat(3, 1fr)"][style*="maxWidth: 700"] { grid-template-columns: repeat(2, 1fr) !important; gap: 12px !important; }
          /* Testimonials */
          .home-marketplace [style*="repeat(2, 1fr)"][style*="gap: 24"] { grid-template-columns: 1fr !important; }
          /* Newsletter in footer */
          footer > div > div:first-child { flex-direction: column !important; gap: 24px !important; }
          footer > div > div:first-child > div { flex: 1 1 100% !important; }
          footer form { flex-direction: column !important; }
          footer form input { width: 100% !important; }
          footer form button { width: 100% !important; }
          /* Footer grid */
          footer [style*="grid"] { grid-template-columns: 1fr !important; gap: 32px !important; }
        }
        .partner-logo-wrap { transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.4s; }
        .partner-logo-wrap:hover { transform: rotate(360deg) scale(1.08); box-shadow: 0 4px 20px rgba(255,122,0,0.2); }
      `}</style>
    </div>
  );
}
